<template>
  <div>
    <b-row v-if="!setGlobalLoading && errorsMessage && errorsMessage.length" class="alert-messages pt-3">
      <b-col>
        <b-alert
          show
          variant="danger"
          v-for="(alertMessage, index) in errorsMessage"
          :key="'alert-msg-' + index.toString()"
          class="alert-height"
        ><div v-html="alertMessage"></div></b-alert>
      </b-col>
    </b-row>
    <StepTitle>
      <div v-html="session.match_message"></div>
      <template #subtitle>
        <div class="customer-form-subtitle">Complete one field to continue</div>
      </template>
    </StepTitle>
    <b-form @submit.prevent="match()">
      <div class="customer-form-wrapper">
        <div v-for="(field, index) in campaign.match_fields" :key="'field-' + field">
          <div v-if="index > 0 && !hasAnyFieldValue" class="field-or-separator">
            <hr />
            <span>or</span>
          </div>
          <b-row v-if="$data[field] || !hasAnyFieldValue" class="justify-content-md-center boxes">
            <b-col class="customer-match-form">
              <div class="customer-match-formgroup">
              <label v-if="field === 'order_number'" :for="field" class="text-capitalize">Order Number</label>
              <label v-else-if="field === 'phone'" :for="field" class="text-capitalize">Phone Number</label>
              <label v-else :for="field" class="text-capitalize">{{ field }}</label>

              <b-form-group v-if="field === 'email'" label-for="email" :class="{highlighted: isFieldHighlighted(field)}">
                <div data-for="email" class="placeholder-label">Email</div>
                <div class="pt-0">
                  <b-form-input
                    id="email"
                    type="email"
                    class="form-input-height"
                    v-model.trim="email"
                    placeholder="example@gmail.com"
                  ></b-form-input>
                </div>
              </b-form-group>
              <b-form-group v-if="field === 'phone'" label-for="phone" :class="{highlighted: isFieldHighlighted(field)}">
                <div data-for="phone" class="placeholder-label">Phone Number</div>
                <b-form-input
                  id="phone"
                  type="tel"
                  v-model.trim="phone"
                  class="form-input-height"
                  placeholder="555-555-5555"
                ></b-form-input>
              </b-form-group>
              <b-form-group v-if="field === 'order_number'" label-for="order_number" :class="{highlighted: isFieldHighlighted(field)}">
                <div data-for="order_number" class="placeholder-label">Order Number</div>
                <b-form-input
                  id="order_number"
                  type="text"
                  class="form-input-height"
                  v-model.trim="order_number"
                  placeholder="123456"
                ></b-form-input>
              </b-form-group>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
      <div class="customer-form-wrapper customer-form-btn-wrapper">
        <button v-if="session && session.can_undo && setGlobalLoading" @click="goBack()" class="btn-base input-choice-btn input-btn new-btn-outline-success stroke-2x" type="button" >Back</button>
        <button class="btn-base input-choice-btn input-btn stroke-2x" @click="$event.stopPropagation()" :class="{'submit-and-verify': !setGlobalLoading}" type="submit">
          <template v-if="setGlobalLoading"> Submit </template>
          <template v-else>
             SUBMIT & VERIFY
             <span class="float-none float-sm-right pr-2">
               <font-awesome-icon :icon="['fas', 'arrow-right']"></font-awesome-icon>
             </span>
          </template>
        </button>
      </div>
    </b-form>
    <div ref="translation_detector" style="display:none;">{{translateDetectString}}</div>
    <b-link block class="centered-block close-button" variant="secondary" v-if="session && session.is_popup && !session.can_undo" @click="goBack()">&times;</b-link>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {CustomerMatchMessages, TranslateTestString} from '@/constants'
import {setPlaceholders, stopMouseflowRecording, getErrorData} from '@/utils'
import StepTitle from './StepTitle'

export default {
  name: 'CustomerMatch',
  components: {StepTitle},
  props: {
    getCart: {
      type: Boolean,
      default: true
    },
    setGlobalLoading: {
      type: Boolean,
      default: true
    }
  },
  data: function() {
    return {
      email: null,
      phone: null,
      order_number: null,
      retryCnt: 0,
      translateDetectString: TranslateTestString,
      placeholders: {
        email: '',
        phone: '',
        order_number: ''
      },
      fieldErrors: {},
      errorsMessage: null,
      highlightedFields: []
    }
  },
  computed: {
    ...mapState(['session', 'campaign', 'isIE']),
    hasAnyFieldValue() {
      return !!(this.email || this.phone || this.order_number)
    }
  },
  methods: {
    resetFieldErrors: function() {
      this.errorsMessage = null
      this.fieldErrors = {}
      this.highlightedFields = []

      // clear the fields and their error counts
      for (let field of this.campaign.match_fields) {
        this.$data[field] = null
        this.fieldErrors[field] = 0
      }
    },
    isFieldHighlighted: function(field) {
      return this.highlightedFields.indexOf(field) >= 0
    },
    getFieldsString: function(fields) {
      let result = ''

      fields.forEach((field, index) => {
        if (index > 0) {
          result += (index === (fields.length - 1)) ? ' or ' : ', '
        }

        result += this.getFieldLabel(field)
      })

      return result
    },
    getFieldLabel: function(field) {
      const fieldLabels = {
        email: 'email address',
        phone: 'phone number'
      }

      return (field in fieldLabels) ? fieldLabels[field] : field.replace('_', ' ')
    },
    getFieldErrors: function() {
      let unusedFields = []
      let errorFields = []
      let message = ''
      let highlightedFields = []

      // increment the errors for each submitted field and get the list of unused fields
      for (let field of this.campaign.match_fields) {
        if (this.$data[field]) {
          this.fieldErrors[field] += 1
          errorFields.push(field)
        } else if (!this.fieldErrors[field]) {
          unusedFields.push(field)
        }
      }

      if ((this.retryCnt === 1) || (unusedFields.length === 0)) {
        message += '<br/>Make sure you typed your ' + this.getFieldsString(errorFields) + ' correctly.'
        highlightedFields = errorFields
      }

      if (unusedFields.length > 1) {
        if (this.retryCnt > 1) {
          // 3rd or greater attempt - clear all the fields
          for (let field of this.campaign.match_fields) {
            this.$data[field] = null
          }

          message += ' Try'
        } else {
          // 2nd attempt
          message += ' Or try'
        }

        message += ' entering your ' + this.getFieldsString(unusedFields) + ' instead.'
        highlightedFields = highlightedFields.concat(unusedFields)
      }

      this.highlightedFields = highlightedFields
      message += " And make sure it's the same as when you placed your order."

      return message
    },
    processPostMatch: function(session) {
      if (this.getCart && session.customer) {
        this.$store.dispatch('get_cart')
      }
      this.$emit('done')
      if (!this.setGlobalLoading) {
        this.$emit('verifying', false)
      }
    },
    anyFieldFilled(params) {
      let filledValues = Object.values(params).filter(value => value !== null && value.length > 0)
      return filledValues.length > 0
    },
    match: function() {
      let params = {}
      for (let field of this.campaign.match_fields) {
        params[field] = this.$data[field]
      }
      if (!this.anyFieldFilled(params)) {
        this.$store.commit('messages', {
          autoHide: true,
          status: 'error',
          isMatchVerifyError: true,
          messages: ['Please fill at least one of the following fields']
        })
        return
      }
      this.$root.$emit('toggle-processing-gifs', CustomerMatchMessages)
      if (this.setGlobalLoading) {
        this.$store.commit('busy')
      } else {
        this.errorsMessage = null
        this.$emit('verifying', true)
      }
      this.$store.dispatch('match', params)
        .then(session => this.processPostMatch(session))
        .catch(err => {
          if (err.response && err.response.status === 404) {
            this.retryCnt++
            let message = "Sorry, but we couldn't find your information."

            if (this.retryCnt < this.session.lifeline_unmatched_count) {
              this.$store.commit('error', message + this.getFieldErrors())
            } else {
              this.$emit('fail', message)
            }
          } else if (err.response && err.response.status === 403) {
            const error = getErrorData(err)
            this.$emit('fail', error.message)
          } else if (err.response && err.response.status === 400) {
            let errors = []

            Object.keys(err.response.data).forEach(key => {
              errors = errors.concat(err.response.data[key])
            })
            if (!this.setGlobalLoading) {
              this.errorsMessage = errors
            } else {
              this.$store.commit('error', errors)
            }
          } else if (err.response && err.response.status === 303 && err.response.data) {
            this.$store.commit('session', err.response.data)
            this.processPostMatch(err.response.data)
          } else {
            this.$store.commit('critical_error', err)
            this.resetFieldErrors()
          }
          if (!this.setGlobalLoading) {
            this.$emit('verifying', false)
          } else {
            this.$store.commit('ready')
          }
        })
        .finally(() => this.$root.$emit('toggle-processing-gifs', null))
    },
    goBack: function() {
      this.$emit('back')
    }
  },
  created() {
    this.resetFieldErrors()
  },
  mounted() {
    stopMouseflowRecording()
    this.$store.dispatch('wait_for_translation', {element: this.$refs['translation_detector']})
      .then(value => {
        setPlaceholders(this.placeholders)
      })
  },
  beforeDestroy() {
    this.$root.$emit('toggle-processing-gifs', null)
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

</style>
